import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Text
} from '@sparrowengg/twigs-react';
import React, { useEffect, useState } from 'react';
import SingleWebhook from './components/webhook';
import { devPortalServices } from 'commons/services';
import { Spinner } from 'commons/components/spinner';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearWebhookApp } from 'redux-store/actions';
import { WebhookDetailsType } from 'features/dashboard/types';
import Fallback from 'commons/components/fall-back';
import TicketIcon from 'commons/icons/ticket';
import { FilterIcon, PlusIcon } from '@sparrowengg/twigs-react-icons';
import StatusIcon from 'commons/icons/status';
import { IRootDispatch } from 'commons/types';
import { filterStates } from './constants';

const Webhook = ({
  webhookList,
  setWebhookList,
  inputSearch
}: {
  webhookList: WebhookDetailsType[];
  setWebhookList: React.Dispatch<React.SetStateAction<WebhookDetailsType[]>>;
  inputSearch: string;
}) => {
  const [loader, setLoader] = useState(true);
  const [filter, setFilter] = useState({
    enabled: false,
    disabled: false
  });
  const navigate = useNavigate();
  const dispatch: IRootDispatch = useDispatch();

  const getAllWebhookApps = async () => {
    try {
      const response = await devPortalServices.getAllWebhookApps();
      setWebhookList(response);
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getAllWebhookApps();
    dispatch(clearWebhookApp());
  }, []);

  const currentWebhook = webhookList.filter((webhook) => {
    const isEnabled =
      filter.enabled && filter.disabled
        ? true
        : filter.enabled
          ? webhook.validated
          : filter.disabled
            ? !webhook.validated
            : true;
  
    return (
      isEnabled &&
      (!inputSearch ||
        webhook?.name?.toLowerCase().includes(inputSearch?.toLowerCase()))
    );
  });

  return (
    <>
      {loader ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ height: 'calc(100vh - 118px - 60px)' }}
        >
          <Spinner size="md" />
        </Flex>
      ) : (
        <Box
          css={{
            paddingBlock: '$2 $38',
            height: 'calc(100vh - 178px)'
          }}
        >
          {webhookList?.length > 0 ? (
            <Flex alignItems="flex-start" gap="$16" css={{ marginTop: '$23' }}>
              <Flex flexDirection="column" gap="$16" css={{ width: 200 }}>
                <Flex alignItems="center" gap="$2">
                  <FilterIcon size={20} color="#6A6A6A" />
                  <Heading size="h6">Apply Filters</Heading>
                </Flex>
                <Flex flexDirection="column" gap="$4">
                  <Flex alignItems="center" gap="$2">
                    <StatusIcon />
                    <Text size="md" weight="medium">
                      By Status
                    </Text>
                  </Flex>
                  <Flex
                    flexDirection="column"
                    css={{
                      label: {
                        color: '$neutral800'
                      },
                      button: {
                        borderColor: '$neutral400'
                      }
                    }}
                  >
                    <Box css={{ padding: '$4 $6' }}>
                      <Checkbox
                        onChange={(value) => {
                          setFilter((prev) => ({ ...prev, enabled: value as boolean }))
                        }}
                      >
                        Enabled
                      </Checkbox>
                    </Box>
                    <Box css={{ padding: '$4 $6' }}>
                      <Checkbox
                        onChange={(value) => {
                          setFilter((prev) => ({ ...prev, disabled: value as boolean }))
                        }}
                      >
                        Disabled
                      </Checkbox>
                    </Box>
                  </Flex>
                </Flex>
              </Flex>
              {currentWebhook.length ? (
                <Flex
                  flexDirection="column"
                  gap="$8"
                  css={{
                    width: '100%',
                    height: 'calc(-178px + 100vh)',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      display: 'none'
                    }
                  }}
                >
                  {currentWebhook.map((webhook: WebhookDetailsType) => (
                    <SingleWebhook
                      webhook={webhook}
                      getAllWebhookApps={getAllWebhookApps}
                    />
                  ))}
                </Flex>
              ) : (
                <Flex
                  justifyContent="center"
                  css={{ marginTop: 160, width: '100%' }}
                >
                  <Fallback
                    isBlackTheme={false}
                    description="Your filters worked too well! Adjust them to find the data you need."
                    title="Filtered Out, but Not Lost!"
                    icon={<TicketIcon />}
                  />
                </Flex>
              )}
            </Flex>
          ) : (
            <Flex
              alignItems="center"
              justifyContent="center"
              gap="$12"
              flexDirection="column"
              css={{ marginTop: 160 }}
            >
              <Fallback
                icon={<TicketIcon />}
                title="Create your first global webhook!"
                description="Start streamlining your workflow for all surveys with just a single configuration"
              />
              <Button
                size="lg"
                leftIcon={<PlusIcon />}
                onClick={() => navigate('/add-webhook')}
              >
                New Webhook
              </Button>
            </Flex>
          )}
        </Box>
      )}
    </>
  );
};

export default Webhook;
